/**
 * SA-1126: how the Roles page groups the permission catalog when some modules
 * are off on this deployment. The server says per permission whether its
 * module is on (`feature_enabled`); the resolver drops a permission whose module
 * is off from every effective set, so the page disables it. A role may still
 * hold one, and a save keeps it: the page never edits what it cannot offer.
 * Pure: no store, no DOM.
 */

export interface PermissionRow {
  key: string
  resource: string
  feature_gate: string | null
  /** Absent from an older server: read as available. */
  feature_enabled?: boolean
}

export interface PermissionGroup<P extends PermissionRow = PermissionRow> {
  resource: string
  permissions: P[]
  /** The rows a role could be given here. */
  available: P[]
}

export function isAvailable(permission: PermissionRow): boolean {
  return permission.feature_enabled !== false
}

/**
 * Groups by resource in the manifest's order. A group with at least one row a
 * role could be given stays with the others (`open`); a group of nothing but
 * unavailable rows goes after them (`unavailable`), in the same order.
 */
export function permissionGroups<P extends PermissionRow>(
  resources: string[],
  permissions: P[],
): { open: PermissionGroup<P>[]; unavailable: PermissionGroup<P>[] } {
  const open: PermissionGroup<P>[] = []
  const unavailable: PermissionGroup<P>[] = []
  for (const resource of resources) {
    const rows = permissions.filter((p) => p.resource === resource)
    const group = { resource, permissions: rows, available: rows.filter(isAvailable) }
    if (rows.length > 0 && group.available.length === 0) unavailable.push(group)
    else open.push(group)
  }
  return { open, unavailable }
}

/** `selected` / `total` over the rows a role could be given; `held` = the unavailable rows it holds. */
export function groupCounts(
  group: PermissionGroup,
  selectedKeys: Set<string>,
): { selected: number; total: number; held: number } {
  const selected = group.available.filter((p) => selectedKeys.has(p.key)).length
  const held = group.permissions.filter((p) => !isAvailable(p) && selectedKeys.has(p.key)).length
  return { selected, total: group.available.length, held }
}

/** Select all: every available row on, or off when all are on; an unavailable row is never touched. */
export function withGroupToggled(group: PermissionGroup, selectedKeys: Set<string>): Set<string> {
  const next = new Set(selectedKeys)
  const allOn = group.available.every((p) => next.has(p.key))
  for (const p of group.available) {
    if (allOn) next.delete(p.key)
    else next.add(p.key)
  }
  return next
}
